import type { KeyboardEvent } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
    segmentClassName,
    segmentedTabPhoneClassName,
    segmentedTrackClassName,
} from './segmented-styles';
import { getTabProps } from './tabs';

export function SegmentedTab({
    id,
    panelId,
    selected,
    icon: Icon,
    label,
    onSelect,
}: {
    id: string;
    panelId: string;
    selected: boolean;
    icon: LucideIcon;
    label: string;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            {...getTabProps({ id, panelId, selected })}
            onClick={onSelect}
            className={cn(segmentClassName(selected), segmentedTabPhoneClassName)}
        >
            <Icon aria-hidden />
            {label}
        </button>
    );
}

export function SegmentedControl<T extends string>({
    label,
    value,
    options,
    onChange,
    className,
}: {
    label: string;
    value: T;
    options: Array<{ value: T; label: string; icon?: LucideIcon }>;
    onChange: (value: T) => void;
    className?: string;
}) {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const buttons = Array.from(
            event.currentTarget.querySelectorAll<HTMLButtonElement>('button'),
        );
        const currentIndex = buttons.findIndex((button) => button === document.activeElement);
        if (currentIndex < 0 || buttons.length === 0) return;

        let nextIndex: number | null = null;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % buttons.length;
        if (event.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        }
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;

        if (nextIndex !== null) {
            event.preventDefault();
            buttons[nextIndex]?.focus();
        }
    };

    return (
        <div
            role="group"
            aria-label={label}
            className={cn(segmentedTrackClassName, 'w-fit', className)}
            onKeyDown={handleKeyDown}
        >
            {options.map((option) => {
                const selected = value === option.value;
                const Icon = option.icon;
                return (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={selected}
                        className={segmentClassName(selected)}
                        onClick={() => onChange(option.value)}
                    >
                        {Icon ? <Icon aria-hidden /> : null}
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
